import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Skeleton, SkuThumb } from "@/components/Shared";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Download } from "lucide-react";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const STATUS_STYLE = {
  "Critical": "bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-400",
  "Low": "bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-400",
  "OK": "bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-400",
  "No stock, no sales": "bg-muted text-muted-foreground",
};

export const InventoryPlanner = () => {
  const [leadTime, setLeadTime] = useState("14");
  const [coverDays, setCoverDays] = useState("30");
  const [data, setData] = useState(null);
  const [filter, setFilter] = useState("");
  const [sort, setSort] = useState("cover_asc");

  const SORTS = {
    cover_asc: { label: "Days of cover ↑", fn: (a, b) => (a.days_of_cover ?? 1e9) - (b.days_of_cover ?? 1e9) },
    ship_desc: { label: "Ship qty ↓", fn: (a, b) => b.recommended_ship - a.recommended_ship },
    velocity_desc: { label: "Sales velocity ↓", fn: (a, b) => b.velocity - a.velocity },
    stock_desc: { label: "Current stock ↓", fn: (a, b) => b.current - a.current },
    name_asc: { label: "Name A–Z", fn: (a, b) => (a.name || a.sku).localeCompare(b.name || b.sku) },
  };

  useEffect(() => {
    setData(null);
    api.get(`/tools/planner?lead_time=${leadTime}&cover_days=${coverDays}`).then(({ data }) => setData(data));
  }, [leadTime, coverDays]);

  const exportPdf = async () => {
    if (!data?.plan?.length) return;
    const rows = [...data.plan].sort(SORTS[sort].fn);
    toast.info("Preparing PDF — fetching product images…");
    const thumbs = {};
    const skus = rows.slice(0, 1000).map((p) => p.sku);
    for (let i = 0; i < skus.length; i += 250) {
      try {
        const { data: batch } = await api.post("/thumbnails/batch", { skus: skus.slice(i, i + 250) });
        Object.entries(batch).forEach(([sku, b64]) => { thumbs[sku] = `data:image/jpeg;base64,${b64}`; });
      } catch { /* continue without this batch */ }
    }
    const { jsPDF } = await import("jspdf");
    const autoTable = (await import("jspdf-autotable")).default;
    const doc = new jsPDF({ orientation: "landscape" });
    doc.setFontSize(16);
    doc.text("FBA Inventory Plan", 14, 16);
    doc.setFontSize(10);
    doc.setTextColor(110);
    doc.text(`Generated ${new Date().toLocaleString("en-IN")}  ·  Lead time: ${leadTime} days  ·  Cover: ${coverDays} days  ·  ${data.plan.length} SKUs  ·  ${data.to_ship} need shipping`, 14, 23);
    autoTable(doc, {
      startY: 28,
      head: [["", "Product", "SKU", "Sales/day", "Current FBA Stock", "Inbound (future)", "Days of cover", "Ship to FBA", "Status"]],
      body: rows.map((p) => [
        "", (p.name || p.sku).slice(0, 55), p.sku, p.velocity, p.fulfillable, p.inbound || "—",
        p.days_of_cover ?? "∞", p.recommended_ship > 0 ? p.recommended_ship : "—", p.status,
      ]),
      styles: { fontSize: 7.5, cellPadding: 1.8, valign: "middle" },
      bodyStyles: { minCellHeight: 11 },
      columnStyles: { 0: { cellWidth: 12 } },
      headStyles: { fillColor: [37, 99, 235] },
      didDrawCell: (h) => {
        if (h.section === "body" && h.column.index === 0) {
          const img = thumbs[rows[h.row.index]?.sku];
          if (img) {
            try { doc.addImage(img, "JPEG", h.cell.x + 1, h.cell.y + 0.5, 10, 10); } catch { /* skip bad image */ }
          }
        }
      },
      didParseCell: (h) => {
        if (h.section === "body" && h.column.index === 8) {
          const v = h.cell.raw;
          if (v === "Critical") h.cell.styles.textColor = [220, 38, 38];
          else if (v === "Low") h.cell.styles.textColor = [217, 119, 6];
        }
      },
    });
    doc.save(`inventory-plan-${new Date().toISOString().slice(0, 10)}.pdf`);
    toast.success("PDF downloaded");
  };

  return (
    <div className="space-y-5" data-testid="inventory-planner">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <p className="text-sm text-muted-foreground">
          Recommended quantities to ship to FBA. Current FBA stock = sellable units at Amazon now; Inbound = future shipments already on their way to Amazon. Days of cover counts both, compared against your 30-day sales velocity.
        </p>
        <div className="flex gap-3 items-center flex-wrap">
          <Button size="sm" variant="outline" className="rounded-full" onClick={exportPdf} disabled={!data?.plan?.length} data-testid="planner-pdf-button">
            <Download className="h-3.5 w-3.5 mr-1.5" aria-hidden="true" />
            Download PDF
          </Button>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            Sort
            <Select value={sort} onValueChange={setSort}>
              <SelectTrigger className="w-40 h-9 rounded-full text-xs" data-testid="planner-sort"><SelectValue /></SelectTrigger>
              <SelectContent>{Object.entries(SORTS).map(([k, v]) => <SelectItem key={k} value={k} data-testid={`planner-sort-${k}`}>{v.label}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            Lead time
            <Select value={leadTime} onValueChange={setLeadTime}>
              <SelectTrigger className="w-24 h-9 rounded-full text-xs" data-testid="planner-lead-time"><SelectValue /></SelectTrigger>
              <SelectContent>{["7", "14", "21", "30"].map((d) => <SelectItem key={d} value={d}>{d} days</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="flex items-center gap-2 text-xs text-muted-foreground">
            Cover
            <Select value={coverDays} onValueChange={setCoverDays}>
              <SelectTrigger className="w-24 h-9 rounded-full text-xs" data-testid="planner-cover-days"><SelectValue /></SelectTrigger>
              <SelectContent>{["15", "30", "45", "60"].map((d) => <SelectItem key={d} value={d}>{d} days</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
      </div>
      {!data ? <Skeleton className="h-64" /> : data.plan.length === 0 ? (
        <p className="p-6 text-sm text-muted-foreground bg-card border border-border rounded-lg">No data yet — needs order-items and FBA stock (both sync automatically).</p>
      ) : (
        <div className="bg-card border border-border rounded-lg shadow-sm overflow-hidden" data-testid="planner-table">
          <div className="p-3 border-b border-border">
            <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search by product name, SKU or ASIN…" data-testid="planner-search-input"
              className="w-full max-w-sm h-9 rounded-full border border-border bg-background px-4 text-sm focus:outline-none focus:ring-2 focus:ring-primary" />
          </div>
          <table className="w-full text-sm">
            <thead className="bg-muted/60 text-xs uppercase tracking-wider text-muted-foreground">
              <tr>{["Product", "SKU", "Sales / day", "Current FBA Stock", "Inbound (future shipments)", "Days of cover", "Ship to FBA", "Status"].map((h) => <th key={h} className="text-left px-4 py-3 font-semibold">{h}</th>)}</tr>
            </thead>
            <tbody>
              {data.plan.filter((p) => ((p.name || "") + p.sku + (p.asin || "")).toLowerCase().includes(filter.toLowerCase())).sort(SORTS[sort].fn).slice(0, 200).map((p) => (
                <tr key={p.sku} className="border-t border-border hover:bg-muted/40 transition-colors" data-testid={`planner-row-${p.sku}`}>
                  <td className="px-4 py-2.5"><div className="flex items-center gap-2.5"><SkuThumb sku={p.sku} alt={p.name || p.sku} /><span className="font-medium max-w-xs truncate">{p.name || p.sku}</span></div></td>
                  <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{p.sku}{p.asin && <span className="block text-[10px] opacity-70">{p.asin}</span>}</td>
                  <td className="px-4 py-2.5">{p.velocity}</td>
                  <td className="px-4 py-2.5 font-semibold">{p.fulfillable}</td>
                  <td className="px-4 py-2.5">{p.inbound > 0 ? p.inbound : <span className="text-muted-foreground">—</span>}</td>
                  <td className="px-4 py-2.5">{p.days_of_cover ?? "∞"}</td>
                  <td className="px-4 py-2.5 font-heading font-bold text-blue-600">{p.recommended_ship > 0 ? p.recommended_ship : "—"}</td>
                  <td className="px-4 py-2.5"><Badge className={`${STATUS_STYLE[p.status]} border-0 font-semibold`}>{p.status}</Badge></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};
